/**
 * supplyRows.ts — the PURE rules for the supplies rows on a prescription.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescription supplies
 * (2026-09-24).
 *
 * Both prescribing surfaces — the ordinary `PrescribeForm` and the temporary
 * `RePrescribeModal` — build their supplies from these functions, so the two
 * cannot seed, validate or post supplies differently. They are pure so the
 * rules can be tested without a DOM, and so a third surface has nothing to copy.
 *
 * R28: not one supply name exists in this file. Names arrive from the server.
 */
import type { PrescribingContext, PrescriptionSupplyInput, SupplySuggestionReason } from '../api/types';
import type { SupplyLine } from '../ui/PrescribedSupplies';

/** Whole-number bounds for one supply's quantity — the server's own (1..50). */
export const MIN_SUPPLY_QUANTITY = 1;
export const MAX_SUPPLY_QUANTITY = 50;
/** The most supplies one prescription may carry — the server's own cap. */
export const MAX_SUPPLIES = 10;

/**
 * Why a row is on the list before the clinician touched it: one of the
 * server's suggestion reasons, or `previous_prescription` — carried over from
 * this case's latest issued prescription (a re-issue). `null` is a row the
 * clinician added themselves. It is COPY, never a control.
 */
export type SupplyRowReason = SupplySuggestionReason | 'previous_prescription';

/**
 * One supply row under construction.
 *
 * The quantity is held as the TEXT the clinician typed, not a number, so a
 * half-typed value ("" while they replace a 4 with a 6) is representable
 * without the input fighting them. It becomes a number only on submit, and only
 * once {@link supplyQuantityValid} says it is one.
 */
export interface SupplyDraft {
  supplyKey: string;
  quantityText: string;
  reason: SupplyRowReason | null;
}

/** A whole number from 1 to 50, typed as digits — nothing else passes. */
export function supplyQuantityValid(text: string): boolean {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return false;
  const value = Number(trimmed);
  return value >= MIN_SUPPLY_QUANTITY && value <= MAX_SUPPLY_QUANTITY;
}

/**
 * The prior prescription's supplies, as this build reads them.
 *
 * ABSENT (an orders build that predates the field) reads exactly like `null` —
 * "no prior supplies to carry" — so the suggestions seed the rows, which is
 * what such a build did anyway.
 */
function priorOf(context: PrescribingContext): PrescriptionSupplyInput[] {
  return Array.isArray(context.priorSupplies) ? context.priorSupplies : [];
}

/**
 * Is this answer one the form can render HONESTLY?
 *
 * ⚠️ THE RESPONSE IS CHECKED, NOT ASSUMED (R41 — "the server would not do that"
 * is not a control). A suggested OR prior supply whose key is not in the
 * catalogue has no name this screen may show, and dropping it quietly would be
 * dropping exactly the kind of supply this feature exists to stop going missing.
 * So that shape, like `suppliesAvailable: false`, is an ERROR — the clinician
 * gets a retry and a block, never a shorter list than the server meant.
 */
export function usableContext(context: PrescribingContext): boolean {
  if (!context.suppliesAvailable) return false;
  const known = new Set(context.catalogue.map((entry) => entry.supplyKey));
  return (
    context.suggestedSupplies.every((suggestion) => known.has(suggestion.supplyKey)) &&
    priorOf(context).every((prior) => known.has(prior.supplyKey))
  );
}

/**
 * The rows a prescription starts with.
 *
 * ⚠️ A RE-ISSUE STARTS FROM WHAT WENT OUT LAST TIME. When this case's latest
 * prescription stored supplies (`priorSupplies`, non-empty), THOSE are the
 * first rows — at the quantities the clinician chose then. Seeding a re-issue
 * from the suggestions alone would quietly swap a clinician's "8" back to the
 * default "4".
 *
 * ⚠️ …AND THEN EVERY SUGGESTION THE PRIOR LIST DOES NOT ALREADY CARRY (review
 * round 2, 2026-09-24). The prior list only says what the LAST document stored;
 * the suggestions say what the server believes THIS prescription needs. They
 * disagree in exactly the case that matters: a first prescription that was sent
 * but never dispensed, then corrected — the prior row may carry the pen supply
 * and no bin, while the server (first-time: no bin has ever reached this
 * patient) suggests one. Prior-only seeding dropped that bin silently. So a
 * suggested key missing from the prior list is ADDED, with its suggested
 * quantity and its reason; a key on both keeps the PRIOR quantity (the
 * clinician's own figure outranks the default). Every row stays one the
 * clinician can change or remove before anything is issued.
 *
 * THE CONSEQUENCE, ACCEPTED: a supply the clinician REMOVED from the last
 * prescription is not on the prior list, so if the server still suggests it —
 * a bin for a patient who is still first-time (no box has reached them yet) —
 * it comes back on the next re-issue, as a suggestion with its reason, for the
 * clinician to remove again. The failure the other way round (a suggestion the
 * server is right about silently missing) is the incident this whole section
 * exists for.
 *
 * No prior list (null, empty, or absent on an older orders build) → the
 * suggestions alone.
 */
export function seedSupplyRows(context: PrescribingContext): SupplyDraft[] {
  const carried: SupplyDraft[] = priorOf(context).map((p) => ({
    supplyKey: p.supplyKey,
    quantityText: String(p.quantity),
    reason: 'previous_prescription',
  }));
  const present = new Set(carried.map((row) => row.supplyKey));
  const suggested: SupplyDraft[] = context.suggestedSupplies
    .filter((s) => !present.has(s.supplyKey))
    .map((s) => ({ supplyKey: s.supplyKey, quantityText: String(s.quantity), reason: s.reason }));
  return [...carried, ...suggested];
}

/**
 * The rows as the WIRE takes them — a key and a whole-number quantity each, and
 * nothing else (R41: the server snapshots the name, unit and repeat rule it
 * holds). Call only once every row is {@link supplyQuantityValid}.
 */
export function suppliesForWire(rows: SupplyDraft[]): PrescriptionSupplyInput[] {
  return rows.map((row) => ({ supplyKey: row.supplyKey, quantity: Number(row.quantityText.trim()) }));
}

/**
 * The rows as a CONFIRMATION shows them: the server's name beside the quantity.
 * Every row's key came from the catalogue (seeded after {@link usableContext}
 * passed, or added from it), so the name is always found; the key fallback only
 * keeps the type total.
 */
export function suppliesSummary(rows: SupplyDraft[], context: PrescribingContext): SupplyLine[] {
  return suppliesForWire(rows).map((supply) => ({
    quantity: supply.quantity,
    name: context.catalogue.find((entry) => entry.supplyKey === supply.supplyKey)?.name ?? supply.supplyKey,
  }));
}
